import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './AboutMassFinder.css';

const AboutMassFinder = () => {
  return (
    <div className="about-finder-page">
      <SEO 
        title="About the Reverent Catholic Mass Finder | Our Mission & Inclusion Criteria"
        description="Learn about the Reverent Catholic Mass Finder, our mission to help Catholics locate reverent liturgy, and our inclusion criteria for all affiliations."
        path="/about-mass-finder"
      />
      
      <section className="about-hero">
        <div className="about-container">
          <Link to="/mass-map" className="back-to-finder">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Mass Finder
          </Link>
          <h1>About the Reverent Catholic Mass Finder</h1>
          <p className="hero-subtitle">
            A comprehensive directory of reverent Catholic Mass locations across North America
          </p>
        </div>
      </section>

      <section className="about-content">
        <div className="about-container about-container-narrow">
          
          {/* Our Mission */}
          <div className="content-section">
            <h2>Our Mission</h2>
            <p>
              The Reverent Catholic Mass Finder exists to help Catholics locate places where the Sacred Liturgy 
              is celebrated with dignity, reverence, and fidelity to the Church's traditions. Whether you are 
              traveling, relocating, or simply seeking a more reverent liturgical experience in your area, 
              this directory provides a comprehensive resource for finding Catholic Mass across North America.
            </p>
            <p>
              The Reverent Catholic Mass Finder includes both Latin and Eastern Catholic churches, 
              reflecting the full liturgical life of the Catholic Church.
            </p>
            <p>
              We believe that the way we worship matters deeply to our faith. Our goal is to connect Catholics 
              with communities where they can encounter the sacred mysteries in an atmosphere of prayerful reverence.
            </p>
          </div>

          {/* Inclusion Criteria */}
          <div className="content-section">
            <h2>Inclusion Criteria</h2>
            <p>
              This directory includes Catholic Mass locations that meet our criteria for reverent celebration 
              of the Sacred Liturgy. We include:
            </p>
            
            <div className="criteria-grid">
              <div className="criteria-card">
                <div className="criteria-badge diocesan">Diocesan</div>
                <h4>Diocesan Latin Mass Communities</h4>
                <p>
                  Parishes and chaplaincies within dioceses that offer the Traditional Latin Mass 
                  (Extraordinary Form) with permission from the local ordinary.
                </p>
              </div>
              
              <div className="criteria-card">
                <div className="criteria-badge fssp">FSSP</div>
                <h4>Priestly Fraternity of St. Peter</h4>
                <p>
                  Parishes and apostolates served by the FSSP, a society of apostolic life in full 
                  communion with Rome, dedicated to the Traditional Latin Mass.
                </p>
              </div>
              
              <div className="criteria-card">
                <div className="criteria-badge icksp">ICKSP</div>
                <h4>Institute of Christ the King</h4>
                <p>
                  Apostolates of the Institute of Christ the King Sovereign Priest, another society 
                  in full communion with Rome that celebrates the Traditional Latin Mass.
                </p>
              </div>
              
              <div className="criteria-card">
                <div className="criteria-badge eastern">Eastern Catholic</div>
                <h4>Eastern Catholic Churches</h4>
                <p>
                  All 23 Eastern Catholic Churches in full communion with Rome, including Byzantine Catholic 
                  (Ruthenian, Romanian, Ukrainian), Maronite, Melkite, Chaldean, Syro-Malabar, Armenian, and 
                  other Eastern rites. These churches celebrate the Divine Liturgy according to their ancient traditions.
                </p>
              </div>
              
              <div className="criteria-card">
                <div className="criteria-badge ordinariate">Ordinariate</div>
                <h4>Personal Ordinariates</h4>
                <p>
                  Communities of the Personal Ordinariate of the Chair of St. Peter, which celebrate 
                  Mass according to Divine Worship: The Missal, a form of the Roman Rite with Anglican patrimony.
                </p>
              </div>
              
            </div>

            <div className="exclusion-note">
              <h4>What We Do Not Include</h4>
              <p>
                This directory does <strong>not</strong> include the Society of St. Pius X (SSPX), 
                sedevacantist groups, independent chapels not in communion with the Catholic Church, 
                or any communities whose sacraments would be of doubtful validity. All listings are 
                reviewed for Catholic sacramental validity.
              </p>
            </div>
          </div>

          {/* Independence */}
          <div className="content-section">
            <h2>Independence</h2>
            <p>
              The Reverent Catholic Mass Finder is an independent project and is <strong>not officially 
              affiliated</strong> with any diocese, religious order, society, or ecclesiastical organization. 
              Inclusion in this directory does not imply endorsement by any Church authority, nor does it 
              constitute an official recommendation.
            </p>
            <p>
              We strive to provide accurate, factual information to help Catholics make informed decisions 
              about where to attend Mass. The responsibility for discerning one's Sunday obligation and 
              spiritual needs remains with each individual Catholic in consultation with their pastor.
            </p>
          </div>

          {/* Accuracy Commitment */}
          <div className="content-section">
            <h2>Accuracy Commitment</h2>
            <p>
              We are committed to maintaining accurate, up-to-date information for all listings in our directory. 
              Mass times, addresses, and contact information can change, and we rely on community feedback to 
              keep our data current.
            </p>
            <div className="accuracy-cta">
              <h4>Help Us Improve</h4>
              <p>
                If you notice any incorrect information, outdated Mass times, or know of a reverent Mass 
                location that should be included, please let us know.
              </p>
              <Link to="/mass-map" className="accuracy-btn">
                Submit a Correction or Suggestion →
              </Link>
            </div>
          </div>

          {/* Reliability System */}
          <div className="content-section">
            <h2>Understanding Reliability Ratings</h2>
            <p>
              Each location in our directory includes a reliability indicator to help you plan your visit:
            </p>
            <div className="reliability-grid">
              <div className="reliability-item high">
                <span className="reliability-badge">High</span>
                <div>
                  <strong>Regular Parish or Priory</strong>
                  <p>Established location with consistent Mass schedule. Most reliable for drop-in visits.</p>
                </div>
              </div>
              <div className="reliability-item medium">
                <span className="reliability-badge">Medium</span>
                <div>
                  <strong>Chapel</strong>
                  <p>Regular schedule but may have limited hours. Consider calling ahead for weekday Masses.</p>
                </div>
              </div>
              <div className="reliability-item low">
                <span className="reliability-badge">Low</span>
                <div>
                  <strong>Mission</strong>
                  <p>Served occasionally by visiting clergy. Always confirm schedule before traveling.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="about-cta">
        <div className="about-container">
          <h2>Ready to Find a Mass?</h2>
          <p>Use our directory to locate a reverent Catholic Mass near you.</p>
          <Link to="/mass-map" className="cta-btn">
            Open the Mass Finder
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutMassFinder;
